import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  FILTER_OPS, RELATIVE_DATE_OPS, isCompleteCondition, normalizeConditionFilter, parseDay, dayToIso,
} from '../../../utils/columnFilterTypes';
import StuiSelect from '../StuiSelect';

// Excel's Custom AutoFilter: "Show rows where <column>" [op][value] (And | Or) [op][value].
// The second condition is optional; Between takes two values; relative date operators take none.

const inputType = (type) => (type === 'number' ? 'number' : type === 'date' ? 'date' : 'text');

// A date <input> wants 'YYYY-MM-DD'; stored values may be any parseable form.
const toInputValue = (type, v) => {
  if (v === undefined || v === null) return '';
  if (type !== 'date') return String(v);
  const day = parseDay(v);
  return day === null ? '' : dayToIso(day);
};

const blankCond = { op: '', v: '', v2: '' };

const ConditionRow = ({ type, cond, onChange, allowNone, onEnter, index }) => {
  const needsValue = !(type === 'date' && RELATIVE_DATE_OPS.has(cond.op)) && cond.op !== '';
  const keyDown = e => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } };
  return (
    <div className="stui-filter-condition-row">
      <StuiSelect
        className="stui-filter-text-operator"
        value={cond.op}
        onChange={e => onChange({ ...cond, op: e.target.value })}
        aria-label={`Condition ${index + 1} operator`}
      >
        {allowNone && <option value="">(none)</option>}
        {FILTER_OPS[type].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </StuiSelect>
      {needsValue && (
        <input
          type={inputType(type)}
          className="stui-filter-text-input"
          value={cond.v}
          onChange={e => onChange({ ...cond, v: e.target.value })}
          onKeyDown={keyDown}
          aria-label={`Condition ${index + 1} value`}
          autoFocus={index === 0}
        />
      )}
      {needsValue && cond.op === 'between' && (
        <>
          <span>and</span>
          <input
            type={inputType(type)}
            className="stui-filter-text-input"
            value={cond.v2}
            onChange={e => onChange({ ...cond, v2: e.target.value })}
            onKeyDown={keyDown}
            aria-label={`Condition ${index + 1} second value`}
          />
        </>
      )}
    </div>
  );
};

/**
 * @param column   { key, label }
 * @param type     'text' | 'number' | 'date'
 * @param filter   the column's current filter (a condition filter pre-fills the dialog)
 * @param presetOp operator picked in the flyout (starts condition 1 with it)
 * @param onApply  (conditionFilter) => void
 * @param onCancel () => void
 */
const CustomFilterDialog = ({ column, type, filter, presetOp, onApply, onCancel }) => {
  const existing = normalizeConditionFilter(filter);
  const sameType = existing && existing.type === type ? existing : null;
  const [join, setJoin] = useState(sameType?.join || 'and');
  const [c1, setC1] = useState(() => {
    const base = sameType?.conds[0];
    const op = presetOp || base?.op || FILTER_OPS[type][0].value;
    return base && (!presetOp || presetOp === base.op)
      ? { op, v: toInputValue(type, base.v), v2: toInputValue(type, base.v2) }
      : { op, v: '', v2: '' };
  });
  const [c2, setC2] = useState(() => {
    const base = !presetOp ? sameType?.conds[1] : null;
    return base ? { op: base.op, v: toInputValue(type, base.v), v2: toInputValue(type, base.v2) } : blankCond;
  });

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const canApply = isCompleteCondition(c1, type);
  const apply = () => {
    if (!canApply) return;
    const conds = [c1];
    if (c2.op && isCompleteCondition(c2, type)) conds.push(c2);
    const f = normalizeConditionFilter({ type, join, conds });
    if (f) onApply(f);
  };

  return (
    <div className="stui-modal-overlay stui-modal-overlay-nested" onMouseDown={e => { if (e.target === e.currentTarget) onCancel?.(); }}>
      <div className="stui-modal stui-modal-md" role="dialog" aria-label="Custom Filter">
        <div className="stui-modal-header">
          <h3>Custom Filter</h3>
          <button type="button" className="stui-modal-close" onClick={onCancel} title="Close"><X size={20} /></button>
        </div>
        <div className="stui-modal-body">
          <p className="stui-modal-hint">Show rows where <strong>{column?.label || column?.key}</strong>:</p>
          <div className="stui-filter-text-body">
            <ConditionRow type={type} cond={c1} onChange={setC1} onEnter={apply} index={0} />
            <div className="stui-filter-join" role="radiogroup" aria-label="Join">
              <label><input type="radio" className="stui-radio-input" name="stui-filter-join" checked={join === 'and'} onChange={() => setJoin('and')} /> And</label>
              <label><input type="radio" className="stui-radio-input" name="stui-filter-join" checked={join === 'or'} onChange={() => setJoin('or')} /> Or</label>
            </div>
            <ConditionRow type={type} cond={c2} onChange={setC2} onEnter={apply} index={1} allowNone />
          </div>
        </div>
        <div className="stui-modal-footer">
          <button type="button" className="stui-btn stui-btn-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="stui-btn stui-btn-primary" onClick={apply} disabled={!canApply}>OK</button>
        </div>
      </div>
    </div>
  );
};

export default CustomFilterDialog;
